
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { SmsStatusIndicator } from '@/components/sms/SmsStatusIndicator';
import { useAuth } from '@/context/auth';
import { useSmsStatus } from '@/context/SmsStatusContext';
import { hasSuperAdminAccess, hasOrgAdminAccess } from '@/context/auth/types';
import { SmsManager } from './tabs/SmsManager';
import { SmsReports } from './tabs/SmsReports';
import CampaignsTab from './tabs/CampaignsTab';
import TriggersTab from './tabs/TriggersTab';
import SubscribersTab from './tabs/SubscribersTab';
import TemplatesTab from './tabs/TemplatesTab';
import PromoCodesTab from './tabs/PromoCodesTab';
import { AlertCircle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';

interface SmsDashboardProps {
  initialTab?: string;
}

const ImprovedSmsDashboard: React.FC<SmsDashboardProps> = ({ initialTab }) => {
  const { user } = useAuth();
  const { restaurantSmsStatus, organisationSmsStatus, loading } = useSmsStatus();
  const organisationId = user?.organisationId;
  
  // Use search params for tab state instead of local storage
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get the active tab from URL parameters or use initial/default
  const tabFromUrl = searchParams.get('tab');
  // Map legacy 'restaurants' tab to 'manage'
  const mappedTabFromUrl = tabFromUrl === 'restaurants' ? 'manage' : tabFromUrl;
  const [activeTab, setActiveTab] = useState(initialTab || mappedTabFromUrl || 'campaigns');
  
  const [tabError, setTabError] = useState<string | null>(null);
  
  // Update active tab when URL parameters change
  useEffect(() => {
    if (tabFromUrl) {
      // Map legacy 'restaurants' value to new 'manage' value
      const newTab = tabFromUrl === 'restaurants' ? 'manage' : tabFromUrl;
      if (newTab !== activeTab) {
        setActiveTab(newTab);
      }
    }
  }, [tabFromUrl]);
  
  // Update active tab when initialTab prop changes
  useEffect(() => {
    if (initialTab && initialTab !== activeTab) {
      setActiveTab(initialTab);
      // Update the URL to match the initialTab
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('tab', initialTab);
      setSearchParams(newSearchParams);
    }
  }, [initialTab, setSearchParams]);
  
  // Determine if user can access management features based on role
  const canAccessManagement = user && (
    hasSuperAdminAccess(user.role) || 
    hasOrgAdminAccess(user.role)
  );
  
  // Set the default tab based on user role
  useEffect(() => {
    if (!canAccessManagement && (activeTab === 'manage' || activeTab === 'triggers' || activeTab === 'reports')) {
      setActiveTab('campaigns');
      // Update URL to match the new tab
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('tab', 'campaigns');
      setSearchParams(newSearchParams);
    }
  }, [canAccessManagement, activeTab]);
  
  const handleTabChange = (value: string) => {
    setActiveTab(value);
    // Update URL with new tab
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('tab', value);
    setSearchParams(newSearchParams);
    // Reset error state when changing tabs
    setTabError(null);
  };

  const handleTabError = (error: Error) => {
    setTabError(error.message || 'An error occurred while loading this tab');
  };

  const handleRetry = () => {
    setTabError(null);
  };
  
  return (
    <div className="space-y-6 min-h-[calc(100vh+1px)] w-full max-w-full overflow-hidden">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4">
            <div>
              <CardTitle>SMS Dashboard</CardTitle>
              <CardDescription>
                Manage your SMS campaigns, templates, subscribers and reports
              </CardDescription>
            </div>
            <div className="sm:flex-shrink-0">
              {organisationId && (
                <SmsStatusIndicator 
                  organisationId={organisationId}
                  showToggle={true}
                  position="ImprovedSmsDashboard-header" 
                />
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
            <TabsList className="grid grid-cols-2 gap-1.5 sm:flex sm:flex-wrap h-auto w-full mb-4">
              {canAccessManagement && (
                <TabsTrigger value="manage" className="sm:flex-[1_0_21%] lg:flex-[1_0_13%]">Manage</TabsTrigger>
              )}
              {canAccessManagement && (
                <TabsTrigger value="triggers" className="sm:flex-[1_0_21%] lg:flex-[1_0_13%]">Triggers</TabsTrigger>
              )}
              <TabsTrigger value="campaigns" className="sm:flex-[1_0_21%] lg:flex-[1_0_13%]">Campaigns</TabsTrigger>
              <TabsTrigger value="templates" className="sm:flex-[1_0_21%] lg:flex-[1_0_13%]">Templates</TabsTrigger>
              <TabsTrigger value="promocodes" className="sm:flex-[1_0_21%] lg:flex-[1_0_13%]">Promo Codes</TabsTrigger>
              <TabsTrigger value="subscribers" className="sm:flex-[1_0_21%] lg:flex-[1_0_13%]">Subscribers</TabsTrigger>
              {canAccessManagement && (
                <TabsTrigger value="reports" className="sm:flex-[1_0_21%] lg:flex-[1_0_13%]">Reports</TabsTrigger>
              )}
            </TabsList>
            
            {tabError && (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Error</AlertTitle>
                <AlertDescription className="flex flex-col gap-2">
                  <p>{tabError}</p>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleRetry}
                    className="w-fit"
                  >
                    Retry
                  </Button>
                </AlertDescription>
              </Alert>
            )}
            
            {canAccessManagement && (
              <TabsContent value="manage">
                <SmsManager />
              </TabsContent>
            )}
            
            {canAccessManagement && (
              <TabsContent value="triggers">
                <TriggersTab onError={handleTabError} />
              </TabsContent>
            )}
            
            <TabsContent value="campaigns">
              <CampaignsTab onError={handleTabError} />
            </TabsContent>
            
            <TabsContent value="templates">
              <TemplatesTab />
            </TabsContent>
            
            <TabsContent value="promocodes">
              <PromoCodesTab />
            </TabsContent>
            
            <TabsContent value="subscribers">
              <SubscribersTab />
            </TabsContent>
            
            {canAccessManagement && (
              <TabsContent value="reports">
                <SmsReports />
              </TabsContent>
            )}
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default ImprovedSmsDashboard;
